'use client';
import React, { useEffect, useRef } from 'react';

interface TrailPoint {
  x: number;
  y: number;
  age: number;
}

export default function ShootingStarCursor() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mousePos = useRef({ x: -100, y: -100 });
  const prevPos = useRef({ x: -100, y: -100 });
  const trail = useRef<TrailPoint[]>([]);
  const animRef = useRef<number>(0);

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e: MouseEvent) => {
      prevPos.current = { ...mousePos.current };
      mousePos.current = { x: e.clientX, y: e.clientY };
      // Add trail points
      trail.current.push({ x: e.clientX, y: e.clientY, age: 0 });
      if (trail.current.length > 22) trail.current.shift();
    };

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Age trail points
      trail.current.forEach(p => { p.age += 1; });
      trail.current = trail.current.filter(p => p.age < 22);

      // Draw comet tail
      if (trail.current.length > 1) {
        for (let i = 1; i < trail.current.length; i++) {
          const p = trail.current[i];
          const prev = trail.current[i - 1];
          const progress = i / trail.current.length;
          const alpha = progress * 0.55;
          const width = progress * 2.5;

          ctx.beginPath();
          ctx.moveTo(prev.x, prev.y);
          ctx.lineTo(p.x, p.y);
          ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
          ctx.lineWidth = width;
          ctx.lineCap = 'round';
          ctx.stroke();

          // Blue glow layer
          ctx.beginPath();
          ctx.moveTo(prev.x, prev.y);
          ctx.lineTo(p.x, p.y);
          ctx.strokeStyle = `rgba(74, 158, 255, ${alpha * 0.6})`;
          ctx.lineWidth = width * 2.5;
          ctx.lineCap = 'round';
          ctx.stroke();
        }
      }

      // Draw cursor dot (star head)
      const mx = mousePos.current.x;
      const my = mousePos.current.y;

      // Outer glow
      const glow = ctx.createRadialGradient(mx, my, 0, mx, my, 12);
      glow.addColorStop(0, 'rgba(74, 158, 255, 0.35)');
      glow.addColorStop(1, 'rgba(74, 158, 255, 0)');
      ctx.beginPath();
      ctx.arc(mx, my, 12, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      // Core dot
      ctx.beginPath();
      ctx.arc(mx, my, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      // Inner blue ring
      ctx.beginPath();
      ctx.arc(mx, my, 5, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(74, 158, 255, 0.7)';
      ctx.lineWidth = 1;
      ctx.stroke();

      animRef.current = requestAnimationFrame(render);
    };

    window.addEventListener('mousemove', handleMouseMove);
    animRef.current = requestAnimationFrame(render);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 99999,
      }}
    />
  );
}